import { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, DollarSign, Zap, Calendar, Users } from 'lucide-react'

interface AnalyticsData {
  dailyUsage: Array<{
    date: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  monthlyUsage: Array<{
    month: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  byTenant: Array<{
    tenantId: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  byKey: Array<{
    secretKey: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  totalRequests: number
  totalTokensInput: number
  totalTokensOutput: number
  totalCost: number
}

const PlatformKeysAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  useEffect(() => {
    let isCurrent = true

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Calculate start date based on time range
        let startDate: string | undefined
        if (timeRange !== 'all') {
          const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
          const date = new Date()
          date.setDate(date.getDate() - days)
          startDate = date.toISOString()
        }

        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)

        const response = await fetch(`/api/admin/platform-keys/analytics?${params}`, {
          credentials: 'include'
        })

        if (response.ok) {
          const data = await response.json()
          if (isCurrent) {
            setAnalytics(data)
          }
        } else {
          throw new Error('Failed to load analytics')
        }
      } catch (err) {
        if (isCurrent) {
          console.error('Error loading analytics:', err)
          setError(err instanceof Error ? err.message : 'Failed to load analytics')
        }
      } finally {
        if (isCurrent) {
          setLoading(false)
        }
      }
    }

    loadData()
    return () => { isCurrent = false }
  }, [timeRange])

  const loadAnalytics = async () => {
    setLoading(true)
    setError(null)

    try {
      // Calculate start date based on time range
      let startDate: string | undefined
      if (timeRange !== 'all') {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
        const date = new Date()
        date.setDate(date.getDate() - days)
        startDate = date.toISOString()
      }

      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)

      const response = await fetch(`/api/admin/platform-keys/analytics?${params}`, {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setAnalytics(data)
      } else {
        throw new Error('Failed to load analytics')
      }
    } catch (err) {
      console.error('Error loading analytics:', err)
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !analytics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error || 'No analytics data available'}
      </div>
    )
  }

  const maxDailyCost = Math.max(...analytics.dailyUsage.map(d => d.estimatedCost), 0.01)
  const maxMonthlyCost = Math.max(...analytics.monthlyUsage.map(m => m.estimatedCost), 0.01)

  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Usage Analytics</h2>
            <p className="text-sm text-gray-600">Token usage and cost tracking</p>
          </div>
        </div>

        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-600">Total Requests</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {analytics.totalRequests.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-600">Total Tokens</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {((analytics.totalTokensInput + analytics.totalTokensOutput) / 1000000).toFixed(2)}M
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {(analytics.totalTokensInput / 1000000).toFixed(2)}M in / {(analytics.totalTokensOutput / 1000000).toFixed(2)}M out
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-600">Estimated Cost</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            ${analytics.totalCost.toFixed(2)}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-gray-600">Active Tenants</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {analytics.byTenant.length}
          </div>
        </div>
      </div>

      {/* Daily Usage Chart */}
      {analytics.dailyUsage.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Daily Cost Trend
          </h3>
          <div className="space-y-2">
            {analytics.dailyUsage.slice(-30).map((day) => (
              <div key={day.date} className="flex items-center gap-3">
                <div className="text-xs text-gray-600 w-20">{day.date}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${(day.estimatedCost / maxDailyCost) * 100}%` }}
                  >
                    {day.estimatedCost > 0 && (
                      <span className="text-xs font-medium text-white">
                        ${day.estimatedCost.toFixed(3)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500 w-16 text-right">
                  {day.totalTokens > 0 ? `${(day.totalTokens / 1000).toFixed(0)}K` : '-'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Summary */}
      {analytics.monthlyUsage.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Summary</h3>
          <div className="space-y-3">
            {analytics.monthlyUsage.map((month) => (
              <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{month.month}</div>
                  <div className="text-sm text-gray-600">
                    {month.requests.toLocaleString()} requests • {(month.totalTokens / 1000000).toFixed(2)}M tokens
                  </div>
                </div>
                <div className="text-lg font-bold text-purple-600">
                  ${month.estimatedCost.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage by Tenant */}
      {analytics.byTenant.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            Usage by Tenant
          </h3>
          <div className="space-y-2">
            {analytics.byTenant.slice(0, 10).map((tenant) => (
              <div key={tenant.tenantId} className="flex items-center gap-3">
                <div className="text-sm text-gray-700 w-40 truncate" title={tenant.tenantId}>
                  {tenant.tenantId === '*' ? 'All Tenants (Wildcard)' : tenant.tenantId}
                </div>
                <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${(tenant.estimatedCost / analytics.totalCost) * 100}%` }}
                  >
                    {tenant.estimatedCost > 0 && (
                      <span className="text-xs font-medium text-white">
                        ${tenant.estimatedCost.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500 w-20 text-right">
                  {tenant.requests} req
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage by API Key */}
      {analytics.byKey.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by API Key</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {analytics.byKey.map((key) => (
              <div key={key.secretKey} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">
                    {key.secretKey === 'claudeApiKey' ? 'Claude API' : 'OpenAI API'}
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    ${key.estimatedCost.toFixed(2)}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {key.requests.toLocaleString()} requests
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {(key.totalTokens / 1000000).toFixed(2)}M tokens ({(key.tokensInput / 1000000).toFixed(2)}M in / {(key.tokensOutput / 1000000).toFixed(2)}M out)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {analytics.totalRequests === 0 && (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No usage data yet</h3>
          <p className="text-gray-600">
            Analytics will appear here once tenants start using platform API keys
          </p>
        </div>
      )}
    </div>
  )
}

export default PlatformKeysAnalytics
